/**
 * 5-Why Analysis Editor Component
 * AI-assisted root cause analysis using GPT-5.2
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  Plus, X, Sparkles, Loader2, Brain, CheckCircle, ChevronRight 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function FiveWhyEditor({ data, onChange, token, onAISuggestApply }) {
  const steps = data?.steps || [];
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  
  const updateStep = (index, field, value) => {
    const updatedSteps = [...steps];
    updatedSteps[index] = { ...updatedSteps[index], [field]: value };
    onChange({ ...data, steps: updatedSteps });
  };
  
  const addStep = () => {
    const newStep = {
      step_number: steps.length + 1,
      why_question: `Why #${steps.length + 1}?`,
      answer: '',
      is_root_cause: false
    };
    onChange({ ...data, steps: [...steps, newStep] });
  };

  const requestAISuggestions = async () => {
    if (!data?.problem_statement) {
      toast.error('Please enter a problem statement first');
      return;
    }
    
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/investigations/ai/suggest-five-why`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          problem_statement: data.problem_statement,
          current_steps: steps.filter(s => s.answer),
          incident_context: null
        })
      });
      
      if (res.ok) {
        const suggestions = await res.json();
        if (suggestions.error) {
          toast.error(suggestions.error);
        } else {
          setAiSuggestions(suggestions);
          toast.success('AI suggestions generated');
        }
      } else {
        toast.error('Failed to get AI suggestions');
      }
    } catch (error) {
      toast.error('Error getting AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestions = () => {
    if (!aiSuggestions?.suggested_steps) return;
    
    const newSteps = aiSuggestions.suggested_steps.map((s, idx) => ({
      step_number: s.step_number || idx + 1,
      why_question: s.why_question,
      answer: s.suggested_answer,
      is_root_cause: idx === aiSuggestions.suggested_steps.length - 1
    }));
    
    onChange({
      ...data,
      steps: newSteps,
      root_cause_summary: aiSuggestions.root_cause_summary || ''
    });
    
    if (onAISuggestApply) {
      onAISuggestApply(aiSuggestions);
    }
    
    setAiSuggestions(null);
    toast.success('AI suggestions applied');
  };

  return (
    <div className="space-y-4">
      {/* AI Assistant Banner */}
      <Card className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-purple-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/30">
                <Brain className="w-5 h-5 text-purple-300" />
              </div>
              <div>
                <h4 className="font-medium text-white">AI Root Cause Assistant</h4>
                <p className="text-sm text-slate-400">GPT-5.2 powered analysis suggestions</p>
              </div>
            </div>
            <Button
              onClick={requestAISuggestions}
              disabled={aiLoading || !data?.problem_statement}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {aiLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Get AI Suggestions</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Suggestions Panel */}
      {aiSuggestions && (
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-purple-300 text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> AI Suggestions
                <Badge className="bg-purple-500/30 text-purple-300">{aiSuggestions.confidence}</Badge>
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAiSuggestions(null)}>Dismiss</Button>
                <Button size="sm" onClick={applyAISuggestions} className="bg-purple-600 hover:bg-purple-700">
                  Apply All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {aiSuggestions.suggested_steps?.map((step, idx) => (
                <div key={idx} className="p-2 rounded bg-slate-900/50">
                  <p className="text-xs text-purple-300">Why #{step.step_number || idx + 1}</p>
                  <p className="text-sm text-slate-300">{step.why_question}</p>
                  <p className="text-xs text-slate-400 mt-1">{step.suggested_answer}</p>
                </div>
              ))}
              {aiSuggestions.root_cause_summary && (
                <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/30 mt-2">
                  <p className="text-xs text-emerald-300 font-medium">Root Cause Summary:</p>
                  <p className="text-sm text-slate-300">{aiSuggestions.root_cause_summary}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Problem Statement</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data?.problem_statement || ''}
            onChange={(e) => onChange({ ...data, problem_statement: e.target.value })}
            placeholder="What is the problem you're investigating?"
            className="bg-slate-900/50 border-slate-600 min-h-[80px]"
          />
        </CardContent>
      </Card>
      
      <div className="space-y-3">
        {steps.map((step, idx) => (
          <Card key={idx} className={`border ${step.is_root_cause ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${step.is_root_cause ? 'bg-emerald-500/30' : 'bg-blue-500/30'}`}>
                  {step.is_root_cause ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <span className="text-sm font-medium text-blue-300">{idx + 1}</span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Input
                    value={step.why_question}
                    onChange={(e) => updateStep(idx, 'why_question', e.target.value)}
                    placeholder={`Why #${idx + 1}?`}
                    className="bg-slate-900/50 border-slate-600 font-medium"
                  />
                  <Textarea
                    value={step.answer || ''}
                    onChange={(e) => updateStep(idx, 'answer', e.target.value)}
                    placeholder="Enter the answer..."
                    className="bg-slate-900/50 border-slate-600 min-h-[60px]"
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={step.is_root_cause}
                        onCheckedChange={(checked) => updateStep(idx, 'is_root_cause', checked)}
                      />
                      <Label className="text-sm text-slate-400">This is the root cause</Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newSteps = steps.filter((_, i) => i !== idx);
                        onChange({ ...data, steps: newSteps });
                      }}
                    >
                      <X className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <ChevronRight className="w-5 h-5 text-slate-500 mt-3" />
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        
        <Button variant="outline" onClick={addStep} className="w-full">
          <Plus className="w-4 h-4 mr-2" /> Add Why Step
        </Button>
      </div>
      
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Root Cause Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data?.root_cause_summary || ''}
            onChange={(e) => onChange({ ...data, root_cause_summary: e.target.value })}
            placeholder="Summarize the root cause(s) identified..."
            className="bg-slate-900/50 border-slate-600 min-h-[80px]"
          />
        </CardContent>
      </Card>
    </div>
  );
}
